/**
 * Devoluciones — gestión de devoluciones / reembolsos.
 */
import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Card, Badge, Button, PageHeader } from '@/components/ui'
import { tenantApi } from '@/lib/api'
import { useAuth } from '@/lib/auth-store'
import { cn } from '@/lib/cn'
import { RotateCcw, Check, Truck, DollarSign, X, Trash2 } from 'lucide-react'

export const Route = createFileRoute('/store/$id/returns')({
  component: ReturnsPage,
})

interface Return {
  id: string
  orderId: string
  orderNumber: string | null
  customerName: string
  customerEmail: string | null
  reason: string
  notes: string | null
  status: 'requested' | 'approved' | 'received' | 'refunded' | 'rejected'
  refundAmount: number | null
  createdAt: string
}

interface ReturnsResponse {
  data: Return[]
  stats: { total: number; requested: number; approved: number; received: number; refunded: number; rejected: number; totalRefunded: number }
}

type Tab = 'all' | 'requested' | 'approved' | 'received' | 'refunded' | 'rejected'

function ReturnsPage() {
  const { activeTenantId } = useAuth()
  const [tab, setTab] = useState<Tab>('requested')

  const { data, isLoading } = useQuery({
    queryKey: ['returns', tab],
    queryFn: () => tenantApi.get<ReturnsResponse>(
      tab === 'all' ? '/api/v1/returns' : `/api/v1/returns?status=${tab}`,
    ),
    enabled: !!activeTenantId,
  })

  const list = data?.data ?? []
  const stats = data?.stats ?? { total: 0, requested: 0, approved: 0, received: 0, refunded: 0, rejected: 0, totalRefunded: 0 }

  return (
    <>
      <PageHeader title="Devoluciones" description="Solicitudes de devolución y reembolsos" />
      <div className="p-8 space-y-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi label="Total" value={stats.total} />
          <Kpi label="Solicitadas" value={stats.requested} color="text-yellow-700 dark:text-yellow-400" />
          <Kpi label="Reembolsadas" value={stats.refunded} color="text-emerald-700 dark:text-emerald-400" />
          <Kpi label="Total reembolsado" value={formatCop(stats.totalRefunded)} isMoney />
        </div>

        <div className="flex items-center gap-1.5 flex-wrap">
          {(['all', 'requested', 'approved', 'received', 'refunded', 'rejected'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={cn(
                'px-3 py-1.5 text-sm rounded-md transition border',
                tab === t ? 'bg-[hsl(var(--muted))] text-foreground border-border' : 'bg-transparent text-muted-foreground border-transparent hover:text-foreground',
              )}
            >
              {t === 'all' ? 'Todas' : t === 'requested' ? 'Solicitadas' : t === 'approved' ? 'Aprobadas' : t === 'received' ? 'Recibidas' : t === 'refunded' ? 'Reembolsadas' : 'Rechazadas'}
            </button>
          ))}
        </div>

        {isLoading && <p className="text-sm text-muted-foreground">Cargando...</p>}
        {!isLoading && list.length === 0 && (
          <Card className="p-12 flex flex-col items-center justify-center text-center">
            <RotateCcw className="size-12 text-muted-foreground mb-3" />
            <p className="text-base font-medium">Sin devoluciones</p>
          </Card>
        )}

        {list.length > 0 && (
          <div className="space-y-3">
            {list.map((r) => <ReturnRow key={r.id} ret={r} />)}
          </div>
        )}
      </div>
    </>
  )
}

function Kpi({ label, value, color, isMoney }: { label: string; value: number | string; color?: string; isMoney?: boolean }) {
  return (
    <Card className="p-5">
      <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
      <p className={cn(isMoney ? 'text-xl font-bold tabular-nums mt-1' : 'text-3xl font-bold tabular-nums mt-1', color)}>{value}</p>
    </Card>
  )
}

function ReturnRow({ ret }: { ret: Return }) {
  const qc = useQueryClient()
  const inv = () => qc.invalidateQueries({ queryKey: ['returns'] })
  const approve = useMutation({ mutationFn: () => tenantApi.post(`/api/v1/returns/${ret.id}/approve`, {}), onSuccess: inv })
  const receive = useMutation({ mutationFn: () => tenantApi.post(`/api/v1/returns/${ret.id}/receive`, {}), onSuccess: inv })
  const reject = useMutation({ mutationFn: () => tenantApi.post(`/api/v1/returns/${ret.id}/reject`, {}), onSuccess: inv })
  const refund = useMutation({
    mutationFn: (amount: number) => tenantApi.post(`/api/v1/returns/${ret.id}/refund`, { refundAmount: amount }),
    onSuccess: inv,
  })

  const date = new Date(ret.createdAt).toLocaleString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' })

  const statusVariant = ret.status === 'refunded' ? 'success' :
    ret.status === 'rejected' ? 'destructive' :
    ret.status === 'requested' ? 'warning' : 'muted'
  const statusLabel = ret.status === 'requested' ? 'Solicitada' : ret.status === 'approved' ? 'Aprobada' : ret.status === 'received' ? 'Recibida' : ret.status === 'refunded' ? 'Reembolsada' : 'Rechazada'

  return (
    <Card className="p-5">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {ret.orderNumber && <p className="font-mono font-bold">{ret.orderNumber}</p>}
            <Badge variant={statusVariant}>{statusLabel}</Badge>
          </div>
          <p className="text-sm mt-1">
            <span className="text-foreground">{ret.customerName}</span>
            {ret.customerEmail && <span className="text-muted-foreground"> · {ret.customerEmail}</span>}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">{date}</p>
        </div>
        {ret.refundAmount !== null && (
          <p className="font-mono font-bold text-emerald-700 dark:text-emerald-400 shrink-0">{formatCop(ret.refundAmount)}</p>
        )}
      </div>

      <div className="bg-[hsl(var(--surface))] rounded p-3 mb-3">
        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Motivo</p>
        <p className="text-sm">{ret.reason}</p>
        {ret.notes && (
          <>
            <p className="text-xs text-muted-foreground uppercase tracking-wider mt-2 mb-1">Notas</p>
            <p className="text-sm text-foreground whitespace-pre-wrap">{ret.notes}</p>
          </>
        )}
      </div>

      <div className="flex items-center gap-2 pt-3 border-t border-border flex-wrap">
        {ret.status === 'requested' && (
          <>
            <Button size="sm" variant="outline" onClick={() => approve.mutate()}>
              <Check className="size-4 mr-2" /> Aprobar
            </Button>
            <Button size="sm" variant="outline" onClick={() => reject.mutate()}>
              <X className="size-4 mr-2" /> Rechazar
            </Button>
          </>
        )}
        {ret.status === 'approved' && (
          <Button size="sm" variant="outline" onClick={() => receive.mutate()}>
            <Truck className="size-4 mr-2" /> Marcar como recibida
          </Button>
        )}
        {(ret.status === 'received' || ret.status === 'approved') && (
          <Button
            size="sm"
            onClick={() => {
              const amount = prompt('Monto del reembolso (en centavos):')
              if (amount && !isNaN(Number(amount))) refund.mutate(Number(amount))
            }}
          >
            <DollarSign className="size-4 mr-2" /> Reembolsar
          </Button>
        )}
      </div>
    </Card>
  )
}

function formatCop(amount: number): string {
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 })
    .format(amount / 100)
}
